import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import '../styles/PropertyCard.css';
import { Bathroom, Bedroom, Chevron, Floodzone, House, Image, LotSize, MapBubble, SquareFootage, YearBuilt } from '../assets/icons';
import { colour, formatterLong, numberFormatter, percentageCompact, percentage, roundToAppropriateDecimals, formatterCompact } from '../styles/GlobalStyles';
import { Badge, Button, Ceiling, CompCardMap, Condition, CustomSwiper, Disqualifier, Distance, FinishChip, Floor, NovationTimeline, PropertyLinks, StaleActive } from './';
import { formatDaysOnMarket, getFlipDetails, getPriceField, getStatusDotColour, propertyConditionBG, propertyStatusBG, propertyStatusText, squareFeetToAcres } from '../functions';
import { getHumanReadableScoreLabel } from '../functions/utils';
import { getTopDrivers } from '../utils/driverUtils';
import { getDriverLabel } from '../utils/driverLabels';
import { kansasCityCountyFIPS } from '../defaults';
import { HugeiconsIcon } from '@hugeicons/react';
import { Calendar03Icon, PoolIcon, CarParking01Icon, CarParking02Icon, BlockGameIcon, Stairs01Icon, Note01Icon } from '@hugeicons/core-free-icons';

function PropertyCard(props) {
    const property = props.property;
    const subjectProperty = props.subjectProperty;
    const subjectPropertyObject = props.subjectPropertyObject;
    // const propertyType = property.propertyAttributes.propertyType;
    const showScrollableImages = props.showScrollableImages;
    const testView = props.testView;
    // Driver badges (e.g. "Finish Level Mismatch") are dev-only: show on localhost / test view, hide in production.
    const showDriverBadges = testView === true || window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const selected = props.selected;
    const toggleSelectedComp = props.toggleSelectedComp;
    const bestComp = props.bestComp;
    const openDetailedView = props.openDetailedView;
    const clickedZPID = props.clickedZPID;
    const clickedCompDescription = props.clickedCompDescription;
    const onChangeZPIDHover = props.onChangeZPIDHover;
    const readOnly = props.readOnly;
    const showKeepRemoveButton = props.showKeepRemoveButton ?? true;
    const isLoaded = props.isLoaded;
    const innerWidth = props.innerWidth;
    const gptLoading = props.gptLoading;
    const priceCeiling = props.priceCeiling;
    const priceCeilingText = props.priceCeilingText === null ? null : typeof props.priceCeilingText === "string" ? props.priceCeilingText : typeof props.priceCeilingText === "object" ? props.priceCeilingText.statement : null;
    const priceFloor = props.priceFloor;
    const priceStaleActive = props.priceStaleActive;
    const darkMode = props.darkMode;
    const conditionUpdate = props.conditionUpdate;
    const allComps = props.allComps;
    const subjectGarage = props.garage;
    const subjectGarageSpaces = props.garageSpaces ?? null;
    const subjectGarageType = props.garageType ?? null;
    const subjectCarport = props.carport;
    const subjectCarportSpaces = props.carportSpaces ?? null;
    const subjectPool = props.pool;
    const purchasePrice = props.purchasePrice || null;
    const propertyEstimates = props.propertyEstimates || null;
    const sourceURL = props.sourceURL || null;
    const source = props.source || null;
    const remodelLevel = props.remodelLevel ?? null;
    const countyId = props.countyId || null;
    const subjectBasementSqFootage = props.basementSqFootage ?? null;
    const onOpenBasementModal = props.onOpenBasementModal || null;
    // Visibility flags for conditional comparison fields. Caller passes the
    // output of usePropertyCardFieldFlags (subject + comps) so every card in a
    // view renders the same field set. Defaults preserve legacy behavior for
    // call sites not yet wired to the hook: flood zone always visible, basement
    // row hidden.
    const showBasement = props.showBasement === true;
    const showFloodZone = props.showFloodZone !== false;
    // Subject's basement presence as a boolean — independent of sqft. Lets the
    // basement row render "Basement" instead of "No basement" when the user
    // flagged the subject as having one (Firestore data.basement / vision
    // analysis) but never recorded the sqft.
    const subjectHasBasement = props.subjectHasBasement === true;
    const problemCodes = Array.isArray(props.problemCodes) ? props.problemCodes : [];
    // Red problem-code badges (e.g. "Year Built Mismatch") are dev-only — show
    // on test view / localhost, hide in production. Same gate as the curb-appeal
    // driver badges above (showDriverBadges).
    const visibleProblemCodes = showDriverBadges ? problemCodes : [];
    const score = typeof props.score === "number" && Number.isFinite(props.score) ? props.score : null;
    const scoreBreakdown = props.scoreBreakdown && typeof props.scoreBreakdown === "object" ? props.scoreBreakdown : null;
    const problemCodeLabels = scoreBreakdown?.problemCodeLabels && typeof scoreBreakdown.problemCodeLabels === "object"
        ? scoreBreakdown.problemCodeLabels
        : null;
    const labelWithNickname = useCallback((code) => {
        const base = getHumanReadableScoreLabel(code);
        const nickname = problemCodeLabels ? problemCodeLabels[code] : null;
        return nickname ? `${base} (${nickname})` : base;
    }, [problemCodeLabels]);
    const [scoreBreakdownOpen, setScoreBreakdownOpen] = useState(false);
    const displayScore = score !== null ? Math.round(score) : null;
    
    // const subdivisions = props.subdivisions || [];
    // const subdivisionObject = subjectProperty === true ? null : subdivisions.find(subdivision => 
    //     subdivision.children && subdivision.children.includes(property.propertyAttributes.subdivision)
    // );
    
    // Find matching curb appeal data by zpid, or use subjectPropertyAnalysis for subject property
    const curbAppealData = useMemo(() => {
        // For subject property, use subjectPropertyAnalysis if available
        if (subjectProperty === true) {
            const subjectAnalysis = props.subjectPropertyAnalysis;
            if (subjectAnalysis) {
                return {
                    positiveDrivers: Array.isArray(subjectAnalysis.positiveDrivers) ? subjectAnalysis.positiveDrivers : [],
                    negativeDrivers: Array.isArray(subjectAnalysis.negativeDrivers) ? subjectAnalysis.negativeDrivers : [],
                    externalPositive: subjectAnalysis.externalPositive || null,
                    externalNegative: subjectAnalysis.externalNegative || null,
                    noiseRating: subjectAnalysis.noiseRating || null,
                    // v5 block-level fields (subject's pairwise fields are always "not_applicable_subject").
                    compBlockBucket: typeof subjectAnalysis.compBlockBucket === "string" ? subjectAnalysis.compBlockBucket : null,
                    vsSubjectBlock: typeof subjectAnalysis.vsSubjectBlock === "string" ? subjectAnalysis.vsSubjectBlock : null,
                    blockImpression: typeof subjectAnalysis.blockImpression === "string" ? subjectAnalysis.blockImpression : null,
                    curbAppealAnalysis: typeof subjectAnalysis.curbAppealAnalysis === "string" ? subjectAnalysis.curbAppealAnalysis : null
                };
            }
            return null;
        }

        // For comps, look up in curbAppeals array
        const curbAppeals = props.curbAppeals || [];
        if (!Array.isArray(curbAppeals) || curbAppeals.length === 0) {
            return null;
        }
        const zpid = property.uniqueIds?.zillowId;
        if (!zpid) return null;
        return curbAppeals.find(item => {
            const itemZpid = typeof item.zpid === "string" ? item.zpid : `${item.zpid}`;
            const propertyZpid = typeof zpid === "string" ? zpid : `${zpid}`;
            return itemZpid === propertyZpid;
        }) || null;
    }, [subjectProperty, props.curbAppeals, props.subjectPropertyAnalysis, property.uniqueIds?.zillowId]);

    // Get top drivers and their labels
    const topDrivers = useMemo(() => {
        if (!curbAppealData) {
            return { topPositive: null, topNegative: null, topPositiveLabel: null, topNegativeLabel: null };
        }
        const drivers = getTopDrivers(curbAppealData);
        return {
            topPositive: drivers.topPositive,
            topNegative: drivers.topNegative,
            topPositiveLabel: drivers.topPositive ? getDriverLabel(drivers.topPositive) : null,
            topNegativeLabel: drivers.topNegative ? getDriverLabel(drivers.topNegative) : null
        };
    }, [curbAppealData]);
    
    // Filter out Google Maps URLs for display (used in branching and passed to swiper).
    // photoUrlsOverride lets callers (e.g. the subject card in RunCompsView) pass a
    // pre-resolved photo set — typically the classifier-mirrored firebaseUrls from
    // propertyData.subjectImageRoomLabels — for off-market properties where AWS
    // has no MLS photos. When provided and non-empty, it wins over AWS.
    const displayablePhotoUrls = useMemo(() => {
        const override = props.photoUrlsOverride;
        if (Array.isArray(override) && override.length > 0) {
            return override.filter(url => typeof url === 'string' && !url.includes("maps.googleapis.com"));
        }
        const urls = property.listingDetails?.mlsPhotoUrls;
        if (!urls || !Array.isArray(urls)) return [];
        return urls.filter(url => !url.includes("maps.googleapis.com"));
    }, [props.photoUrlsOverride, property.listingDetails?.mlsPhotoUrls]);
    
    // Memoize expensive condition lookups
    const conditionUpdateIndex = useMemo(() => 
        subjectProperty === true ? -1 : conditionUpdate.findIndex(e => e.zpid === property.uniqueIds.zillowId),
        [subjectProperty, conditionUpdate, property.uniqueIds.zillowId]
    );
    
    const finalCondition = useMemo(() => {
        if (subjectProperty === true) return null;
        if (conditionUpdateIndex !== -1) return conditionUpdate[conditionUpdateIndex].condition;
        return property.compDetails?.propertyCondition ?? null;
    }, [subjectProperty, conditionUpdateIndex, conditionUpdate, property.compDetails]);

    // Did arvAgent actually run a condition classifier (image or description)
    // for this comp? The backend (buildConditionUpdate) marks real
    // classifications — including a genuine inconclusive "Unclear" result —
    // with analysisAttempted:true, and the stub backfill (no classifier ran)
    // with analysisAttempted:false. We only show the "Unclear" badge when the
    // comp was processed-and-inconclusive; an un-processed comp renders plain
    // "unclear" text with no badge. Falling back to compDetails (no
    // conditionUpdate entry at all) counts as not-processed.
    const conditionProcessed = useMemo(() => {
        if (subjectProperty === true) return false;
        if (conditionUpdateIndex === -1) return false;
        return conditionUpdate[conditionUpdateIndex].analysisAttempted === true;
    }, [subjectProperty, conditionUpdateIndex, conditionUpdate]);

    // The condition badge only renders for a real, processed condition. An
    // un-processed comp whose condition is missing or "Unclear" shows plain
    // lowercase "unclear" text instead (no badge). A non-"Unclear" condition
    // carried from upstream AWS data still shows its badge as before.
    const showConditionBadge = useMemo(() => {
        if (subjectProperty === true) return false;
        if (finalCondition == null) return false;
        if (finalCondition === "Unclear") return conditionProcessed;
        return true;
    }, [subjectProperty, finalCondition, conditionProcessed]);
    
    const propertyConditionFinish = useMemo(() => 
        subjectProperty === true ? null : conditionUpdateIndex !== -1 ? conditionUpdate[conditionUpdateIndex].remodelTier : null,
        [subjectProperty, conditionUpdateIndex, conditionUpdate]
    );
    
    // Memoize flip details calculation
    const flipDetails = useMemo(() => 
        subjectProperty === true ? null : getFlipDetails(property),
        [subjectProperty, property]
    );
    
    const flipStatus = flipDetails === null ? null : flipDetails.flipStatus;
    const flipBGColour = flipDetails === null ? null : darkMode === true ? flipDetails.flipBGColourDark : flipDetails.flipBGColour;
    const flipDescription = flipDetails === null ? null : flipDetails.flipDescription;
    const flipDotcolour = flipDetails === null ? null : flipDetails.flipDotcolour;
    
    // Check if property is in Kansas City metro counties - for subject property grade breakdown
    const isKansasCityCounty = useMemo(() => {
        if (subjectProperty !== true) return false;
        if (!countyId) return false;
        const countyIdStr = typeof countyId === 'string' ? countyId : String(countyId);
        return kansasCityCountyFIPS.includes(countyIdStr);
    }, [subjectProperty, countyId]);
    
    // Memoize above/below grade finished area
    const aboveGradeFinishedArea = useMemo(() => {
        const value = property.propertyAttributes?.aboveGradeFinishedArea;
        if (typeof value === 'number' && value > 0) return value;
        // No usable above-grade figure — either the field is absent, or it's
        // a placeholder 0 that would otherwise render as "0  -100%" on a card
        // whose total sqft is plainly non-zero. Fall back to the total living
        // area: with no stated split, all of the living area is the best
        // available stand-in for above-grade. Subsumes the old KC-only
        // fallback (KC listings report livingSquareFootage as above-grade-only
        // by local convention).
        const total = property.propertyAttributes?.livingSquareFootage;
        if (typeof total === 'number' && total > 0) return total;
        return null;
    }, [property.propertyAttributes]);

    const belowGradeFinishedArea = useMemo(() => {
        // For subject property: a manual entry (Verify Property / basement
        // modal) wins when present — a human's correction over automated data.
        if (subjectProperty === true) {
            if (subjectBasementSqFootage !== null && typeof subjectBasementSqFootage === 'number' && subjectBasementSqFootage > 0) {
                return subjectBasementSqFootage;
            }
            // Otherwise fall back to the automated MLS-sourced split, in any
            // county — the nationwide grade-split fix (2026-08-12) means this
            // is no longer subject-side-blind. Gate on > 0, not truthiness: a
            // stated 0 means "no finished area below grade" on an unfinished
            // basement, not "unknown".
            const automated = property.propertyAttributes?.belowGradeFinishedArea;
            return typeof automated === 'number' && automated > 0 ? automated : null;
        }
        // For comp properties, use belowGradeFinishedArea if available
        const value = property.propertyAttributes?.belowGradeFinishedArea;
        return typeof value === 'number' ? value : null;
    }, [subjectProperty, subjectBasementSqFootage, property.propertyAttributes]);
    
    // Whether to render the above/below grade rows at all. Deliberately keyed
    // off the RAW field rather than the memo above: since that memo now falls
    // back to living sqft, keying off it would sprout an empty breakdown on
    // every card that merely has a total sqft. A stated above-grade field
    // (even a placeholder 0, whose value the fallback repairs) or the KC
    // subject convention is what earns the rows.
    const showGradeBreakdown = useMemo(() => {
        if (aboveGradeFinishedArea === null) return false;
        if (typeof property.propertyAttributes?.aboveGradeFinishedArea === 'number') return true;
        return subjectProperty === true && isKansasCityCounty === true;
    }, [aboveGradeFinishedArea, subjectProperty, isKansasCityCounty, property.propertyAttributes]);

    // Headline square footage: total living sqft by default, UNLESS a real
    // above/below grade breakdown exists (both sides confirmed, below > 0)
    // — then show above-grade only, matching what the breakdown rows and
    // comping already use. Applies to subject and comp cards alike.
    //
    // Deliberately NOT above + below: summing assumes the below-grade area
    // is additive to the stated living total, which isn't always true (a
    // real MLS listing can state a split that already sums to the total AND
    // separately claim in its free-text description that the basement is
    // "not included" — the two disagree). Above-grade alone sidesteps that
    // ambiguity instead of silently inflating the label.
    const totalSquareFootageForLabel = useMemo(() => {
        if (typeof aboveGradeFinishedArea === 'number' && aboveGradeFinishedArea > 0
            && typeof belowGradeFinishedArea === 'number' && belowGradeFinishedArea > 0) {
            return aboveGradeFinishedArea;
        }
        return property.propertyAttributes?.livingSquareFootage || 0;
    }, [aboveGradeFinishedArea, belowGradeFinishedArea, property.propertyAttributes]);
    
    const cardRef = useRef();
    const [showMap, setShowMap] = useState(false);
    const [descriptionOpen, setDescriptionOpen] = useState(false);

    // Free-text MLS remarks for a comp. Collapsed by default — the remarks are
    // long, unstructured and vary wildly in quality, so they'd drown the
    // aligned attribute rows above if always shown. Upstream blanks the field
    // to "" when listingDescriptionValidFlag is false (see
    // src/functions/property/index.js), so trim-check rather than null-check.
    // Subject cards don't get one: the user already knows their own listing.
    const listingDescription = useMemo(() => {
        if (subjectProperty === true) return null;
        const text = property.listingDetails?.listingDescription;
        if (typeof text !== "string" || text.trim().length === 0) return null;
        return text.trim();
    }, [subjectProperty, property.listingDetails]);
    const distanceHoverTimeoutRef = useRef(null);
    
    // Memoize construction type and flood zone codes.
    // For the subject card, `constructionTypeOverride` (sourced from
    // arvData.subjectFeatures.constructionType — set by arvAgent's
    // postProcessor after the construction-LLM call confirms or corrects
    // the AWS value) wins over the AWS-derived string. Lets the card show
    // the post-image-analysis verdict (e.g. "Brick veneer") instead of the
    // upstream supplier's (sometimes wrong) prior. Comp cards continue to
    // use the AWS hierarchy unchanged — comp construction is verified by
    // the comp-image flow, not by this prop.
    const constructionTypeOverride = props.constructionTypeOverride;
    const constructionType = useMemo(() => {
        if (subjectProperty === true && typeof constructionTypeOverride === "string" && constructionTypeOverride.trim().length > 0) {
            return constructionTypeOverride;
        }
        return property.propertyAttributes.constructionTypeBySource === undefined || property.propertyAttributes.constructionTypeBySource === null || property.propertyAttributes.constructionTypeBySource.reapi === null
            ? property.propertyAttributes.constructionType
            : property.propertyAttributes.constructionTypeBySource.reapi;
    }, [subjectProperty, constructionTypeOverride, property.propertyAttributes.constructionType, property.propertyAttributes.constructionTypeBySource]);
    
    const floodZoneCodes = useMemo(() => {
        const floodZoneType = property.propertyAttributes.floodZoneType ? property.propertyAttributes.floodZoneType : "D";
        return typeof floodZoneType === "string" ? floodZoneType : floodZoneType.join(",");
    }, [property.propertyAttributes.floodZoneType]);

    // isKansasCityCounty above is gated to subjectProperty === true, so comp
    // cards need their own check for the KC fallback below.
    const isKansasCityCountyForComp = useMemo(() => {
        if (!countyId) return false;
        const countyIdStr = typeof countyId === 'string' ? countyId : String(countyId);
        return kansasCityCountyFIPS.includes(countyIdStr);
    }, [countyId]);

    // Square footage basis for comp-vs-subject comparison. Generalized
    // 2026-08-12: previously Kansas-City-only (comping ignored a real grade
    // split everywhere else, comparing a basement-inclusive total against a
    // basement-inclusive total — apples to apples, but the wrong apples).
    // Now: use above-grade whenever a real split is stated (any county),
    // matching the backend swap in compFetcherV1's score_and_format.
    //
    // KC fallback: the original KC-only logic used aboveGradeFinishedArea
    // whenever present, with no below-grade requirement — mirrors the same
    // fallback restored in compFetcherV1's score_and_format (comp-side
    // _use_above_grade). Keep it here too so a KC comp missing an explicit
    // below-grade value doesn't silently fall back to a basement-inclusive
    // total it wouldn't have used before.
    const compSquareFootageForComparison = useMemo(() => {
        if (subjectProperty === true) return null;
        const above = property.propertyAttributes?.aboveGradeFinishedArea;
        const below = property.propertyAttributes?.belowGradeFinishedArea;
        if (typeof above === 'number' && above > 0
            && ((typeof below === 'number' && below > 0) || isKansasCityCountyForComp === true)) {
            return above;
        }
        return property.propertyAttributes?.livingSquareFootage;
    }, [subjectProperty, property.propertyAttributes, isKansasCityCountyForComp]);

    const subjectSquareFootageForComparison = useMemo(() => {
        const attrs = subjectPropertyObject.propertyAttributes;
        const above = attrs?.aboveGradeFinishedArea;
        const below = attrs?.belowGradeFinishedArea;
        if (typeof above === 'number' && above > 0 && typeof below === 'number' && below > 0) {
            return above;
        }
        return attrs?.livingSquareFootage;
    }, [subjectPropertyObject.propertyAttributes]);

    const subjectBelowGradeForComparison = useMemo(() => {
        // Manual entry wins when present, else fall back to the automated
        // MLS-sourced split — mirrors the belowGradeFinishedArea memo above.
        if (subjectBasementSqFootage !== null && typeof subjectBasementSqFootage === 'number' && subjectBasementSqFootage > 0) {
            return subjectBasementSqFootage;
        }
        const automated = subjectPropertyObject.propertyAttributes?.belowGradeFinishedArea;
        return typeof automated === 'number' && automated > 0 ? automated : null;
    }, [subjectBasementSqFootage, subjectPropertyObject.propertyAttributes]);

    // Function to generate garage label based on available data
    const getGarageLabel = useCallback((isSubject, hasGarage, propertyAttributes, subjectGarageSpaces, subjectGarageType) => {
        // For subject property: use detailed garage info if available
        if (isSubject === true) {
            if (hasGarage !== true) {
                return null;
            }
            
            // Check for subjectGarageSpaces (integer) and subjectGarageType ("attached"/"detached")
            const hasSpaces = subjectGarageSpaces !== null && subjectGarageSpaces !== undefined && typeof subjectGarageSpaces === 'number' && subjectGarageSpaces > 0;
            const hasType = subjectGarageType === "attached" || subjectGarageType === "detached";
            
            // If we have both spaces and type info
            if (hasSpaces && hasType) {
                const typeAbbr = subjectGarageType === "attached" ? "att." : "det.";
                return `${subjectGarageSpaces} car garage (${typeAbbr})`;
            }
            
            // If we only have spaces info
            if (hasSpaces) {
                return `${subjectGarageSpaces} car garage`;
            }
            
            // If we only know there's a garage (no spaces or type info)
            return "Garage";
        }
        
        // For comp properties: return null if no garage
        if (hasGarage !== true) {
            return null;
        }
        
        // Check for garageSpaces (integer) and garageAttachment ("attached"/"detached")
        const garageSpaces = propertyAttributes?.garageSpaces;
        const garageAttachment = propertyAttributes?.garageAttachment;
        
        const hasSpaces = garageSpaces !== null && garageSpaces !== undefined && typeof garageSpaces === 'number' && garageSpaces > 0;
        const hasAttachment = garageAttachment === "attached" || garageAttachment === "detached";
        
        // If we have both spaces and attachment info
        if (hasSpaces && hasAttachment) {
            const attachmentAbbr = garageAttachment === "attached" ? "att." : "det.";
            return `${garageSpaces} car garage (${attachmentAbbr})`;
        }
        
        // If we only have spaces info
        if (hasSpaces) {
            return `${garageSpaces} car garage`;
        }
        
        // If we only know there's a garage (no spaces or attachment info)
        return "Garage";
    }, []);

    // Memoize garage label
    const garageLabel = useMemo(() =>
        getGarageLabel(subjectProperty, subjectProperty === true ? subjectGarage : property.propertyAttributes.garage, property.propertyAttributes, subjectGarageSpaces, subjectGarageType),
        [subjectProperty, subjectGarage, property.propertyAttributes, getGarageLabel, subjectGarageSpaces, subjectGarageType]
    );

    // Function to generate carport label based on available data
    const getCarportLabel = useCallback((isSubject, hasCarport, propertyAttributes, subjectCarportSpaces) => {
        if (isSubject === true) {
            if (hasCarport !== true) {
                return null;
            }
            const hasSpaces = subjectCarportSpaces !== null && subjectCarportSpaces !== undefined && typeof subjectCarportSpaces === 'number' && subjectCarportSpaces > 0;
            if (hasSpaces) {
                return `${subjectCarportSpaces} car carport`;
            }
            return "Carport";
        }

        if (hasCarport !== true) {
            return null;
        }

        const carportSpaces = propertyAttributes?.carportSpaces;
        const hasSpaces = carportSpaces !== null && carportSpaces !== undefined && typeof carportSpaces === 'number' && carportSpaces > 0;
        if (hasSpaces) {
            return `${carportSpaces} car carport`;
        }
        return "Carport";
    }, []);

    // Memoize carport label — hide carport if the property has a garage
    const carportLabel = useMemo(() => {
        const hasGarage = subjectProperty === true ? subjectGarage : property.propertyAttributes.garage;
        if (hasGarage === true) return null;
        return getCarportLabel(subjectProperty, subjectProperty === true ? subjectCarport : property.propertyAttributes.carport, property.propertyAttributes, subjectCarportSpaces);
    }, [subjectProperty, subjectGarage, subjectCarport, property.propertyAttributes, getCarportLabel, subjectCarportSpaces]);

    // Memoize details array to avoid recalculating on every render
    const details = useMemo(() => [
        // {
        //     label: propertyTypeFormat(propertyType),
        //     bold: null,
        //     icon: <PropertyIcon propertyType={propertyType} fill={colour.gray04} className="" size={14} />,
        //     difference: null
        // },
        {
            label: `${property.propertyAttributes.bedrooms} beds`,
            bold: null,
            icon: <Bedroom className="" size={14} fill={colour.gray04} />,
            difference: subjectProperty === true ? null : property.propertyAttributes.bedrooms - subjectPropertyObject.propertyAttributes.bedrooms,
            differenceNumber: subjectProperty === true ? null : property.propertyAttributes.bedrooms - subjectPropertyObject.propertyAttributes.bedrooms
        },
        {
            label: `${property.propertyAttributes.bathrooms} baths`,
            bold: null,
            icon: <Bathroom className="" size={14} fill={colour.gray04} />,
            difference: subjectProperty === true ? null : roundToAppropriateDecimals(property.propertyAttributes.bathrooms - subjectPropertyObject.propertyAttributes.bathrooms),
            differenceNumber: subjectProperty === true ? null : roundToAppropriateDecimals(property.propertyAttributes.bathrooms - subjectPropertyObject.propertyAttributes.bathrooms)
        },
        {
            label: `${numberFormatter.format(totalSquareFootageForLabel)} sqft`,
            bold: null,
            icon: <SquareFootage fill={colour.gray04} className="" />,
            difference: subjectProperty === true || compSquareFootageForComparison === null || subjectSquareFootageForComparison === null ? null : percentageCompact.format(((compSquareFootageForComparison - subjectSquareFootageForComparison) / subjectSquareFootageForComparison)),
            differenceNumber: subjectProperty === true || compSquareFootageForComparison === null || subjectSquareFootageForComparison === null ? null : compSquareFootageForComparison - subjectSquareFootageForComparison,
            showGradeBreakdown: showGradeBreakdown
        },
        {
            label: `Built ${property.propertyAttributes.yearBuilt}`,
            bold: null,
            icon: <YearBuilt fill={colour.gray04} className="" />,
            difference: subjectProperty === true ? null : `${property.propertyAttributes.yearBuilt - subjectPropertyObject.propertyAttributes.yearBuilt} yr`,
            differenceNumber: subjectProperty === true ? null : property.propertyAttributes.yearBuilt - subjectPropertyObject.propertyAttributes.yearBuilt
        },
        {
            label: `${squareFeetToAcres(property.propertyAttributes.lotSquareFootage)}`,
            bold: null,
            icon: <LotSize fill={colour.gray04} className="" />,
            difference: subjectProperty === true || subjectPropertyObject.propertyAttributes.lotSquareFootage === null || property.propertyAttributes.lotSquareFootage === null ? null : percentageCompact.format(((property.propertyAttributes.lotSquareFootage - subjectPropertyObject.propertyAttributes.lotSquareFootage) / subjectPropertyObject.propertyAttributes.lotSquareFootage)),
            differenceNumber: subjectProperty === true || subjectPropertyObject.propertyAttributes.lotSquareFootage === null || property.propertyAttributes.lotSquareFootage === null ? null : property.propertyAttributes.lotSquareFootage - subjectPropertyObject.propertyAttributes.lotSquareFootage
        },
        {
            label: garageLabel,
            bold: null,
            icon: <HugeiconsIcon icon={CarParking01Icon} size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            label: carportLabel,
            bold: null,
            icon: <HugeiconsIcon icon={CarParking02Icon} size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            label: subjectProperty === true && subjectPool === true ? "Pool" : subjectProperty === false && property.propertyAttributes.pool === true ? "Pool" : null,
            bold: null,
            icon: <HugeiconsIcon icon={PoolIcon} size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            // Basement row. Surfaces a comparison signal whenever subject or any
            // comp has a basement (decided upstream by usePropertyCardFieldFlags
            // and passed in as showBasement). When the flag is on, every card
            // renders the row — value if known, "No basement" otherwise — so
            // the absence is itself a visible signal.
            label: (() => {
                if (showBasement !== true) return null;
                // Sqft source: the belowGradeFinishedArea memo above already
                // handles both sides — subject prefers a manual entry
                // (Verify Property / basement modal), falling back to the
                // automated MLS-sourced split (any county); comps use AWS
                // directly.
                const sqft = belowGradeFinishedArea;
                if (sqft !== null && sqft > 0) return `${numberFormatter.format(sqft)} sqft basement`;
                // Sqft unknown — fall back to the basement boolean. Subject uses
                // the explicit subjectHasBasement prop (Firestore boolean /
                // vision); comps use propertyAttributes.basement.
                if (subjectProperty === true && subjectHasBasement === true) return "Basement";
                if (subjectProperty === false && property.propertyAttributes?.basement === true) return "Basement";
                return "No basement";
            })(),
            bold: null,
            icon: <HugeiconsIcon icon={Stairs01Icon} size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            label: constructionType,
            bold: null,
            icon: <HugeiconsIcon icon={BlockGameIcon} size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            // Flood zone is only material when something interesting is happening
            // across the subject + comp set — usePropertyCardFieldFlags decides
            // (high-risk zone present anywhere, or buckets differ).
            label: showFloodZone === true ? `Flood Zone ${floodZoneCodes}` : null,
            bold: null,
            icon: <Floodzone className="property-card-floodzone-icon" fill={darkMode === true ? colour.gray04 : colour.gray05} />,
            difference: null,
            differenceNumber: null,
        },
        {
            label: subjectProperty === true ? null : property.listingDetails.listingStatus === "for_sale" || property.listingDetails.listingStatus === "pending" ? "Listed" : "Sold",
            bold: subjectProperty === true ? null : property.listingDetails.listingStatus === "for_sale" || property.listingDetails.listingStatus === "pending" ? formatDaysOnMarket(property.listingDetails.saleListingDate) : formatDaysOnMarket(property.listingDetails.soldListingDate),
            icon: subjectProperty === true ? null : <HugeiconsIcon icon={Calendar03Icon} color={colour.gray04} className="" size={16} />,
            difference: null,
            differenceNumber: null
        }
    ], [
        property.propertyAttributes.bedrooms,
        property.propertyAttributes.bathrooms,
        property.propertyAttributes.yearBuilt,
        property.propertyAttributes.lotSquareFootage,
        property.propertyAttributes.pool,
        property.listingDetails.listingStatus,
        property.listingDetails.saleListingDate,
        property.listingDetails.soldListingDate,
        subjectProperty,
        subjectPropertyObject.propertyAttributes.bedrooms,
        subjectPropertyObject.propertyAttributes.bathrooms,
        subjectPropertyObject.propertyAttributes.yearBuilt,
        subjectPropertyObject.propertyAttributes.lotSquareFootage,
        subjectPool,
        constructionType,
        floodZoneCodes,
        darkMode,
        showGradeBreakdown,
        compSquareFootageForComparison,
        subjectSquareFootageForComparison,
        totalSquareFootageForLabel,
        garageLabel,
        carportLabel,
        showBasement,
        showFloodZone,
        subjectHasBasement,
        belowGradeFinishedArea,
        property.propertyAttributes.basement
    ]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (distanceHoverTimeoutRef.current) {
                clearTimeout(distanceHoverTimeoutRef.current);
            }
        };
    }, []);

    const turnOnMap = useCallback(() => {
        setShowMap(true);
    }, []);

    const turnOffMap = useCallback(() => {
        setShowMap(false);
        if (distanceHoverTimeoutRef.current) {
            clearTimeout(distanceHoverTimeoutRef.current);
            distanceHoverTimeoutRef.current = null;
        }
    }, []);

    // Memoize event handlers to avoid creating new functions on every render
    const handleMouseEnter = useCallback(() => {
        if (onChangeZPIDHover !== null) {
            onChangeZPIDHover(property.uniqueIds.zillowId);
        }
    }, [onChangeZPIDHover, property.uniqueIds.zillowId]);

    const handleMouseLeave = useCallback(() => {
        if (onChangeZPIDHover !== null) {
            onChangeZPIDHover(null);
        }
    }, [onChangeZPIDHover]);

    const handleImageClick = useCallback(() => {
        if (showScrollableImages !== null) {
            showScrollableImages(property);
        }
    }, [showScrollableImages, property]);

    const handleBodyClick = useCallback(() => {
        if (subjectProperty === false && openDetailedView !== null) {
            openDetailedView(property);
        }
    }, [subjectProperty, openDetailedView, property]);

    // stopPropagation: the whole card body opens the detailed view on click,
    // and expanding the remarks in place shouldn't also navigate away.
    const handleDescriptionToggle = useCallback((e) => {
        e.stopPropagation();
        setDescriptionOpen(prev => !prev);
    }, []);

    const handleDisqualifierClick = useCallback(() => {
        if (openDetailedView !== null) {
            openDetailedView(property);
        }
    }, [openDetailedView, property]);

    return (
        <div
            className={"property-card-container " + (darkMode === true ? "bg-colour-dark-mode " : subjectProperty === true ? "bg-colour-x-light " : "bg-colour-white property-card-border ") + (subjectProperty === false && clickedZPID === property.uniqueIds.zillowId ? "property-card-outer-border " : "")}
            ref={cardRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-comp-zpid={subjectProperty === true ? undefined : property.uniqueIds?.zillowId}
        >
            <div className="property-card-inner-container">
                {
                    (subjectProperty === false && displayScore !== null) || visibleProblemCodes.length > 0 ?
                    <div className="property-card-problem-codes-container">
                        {
                            subjectProperty === false && displayScore !== null &&
                            (scoreBreakdown ? (
                                <span
                                    className="property-card-score property-card-score-clickable xs-label-bold colour-white bg-colour-jade-03-dark cursor-pointer"
                                    onClick={(e) => { e.stopPropagation(); setScoreBreakdownOpen(prev => !prev); }}
                                >
                                    Score {displayScore} {scoreBreakdownOpen ? "▾" : "▸"}
                                </span>
                            ) : (
                                <span className="property-card-score xs-label-bold colour-white bg-colour-jade-03-dark">
                                    Score {displayScore}
                                </span>
                            ))
                        }
                        {visibleProblemCodes.map((code, i) => (
                            <span key={`pc-${i}`} className="property-card-problem-code xs-label-bold colour-white">
                                {labelWithNickname(code)}
                            </span>
                        ))}
                    </div>
                    : null
                }
                {
                    scoreBreakdownOpen && scoreBreakdown && subjectProperty === false &&
                    <div
                        className="property-card-score-breakdown"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {typeof scoreBreakdown.baseScore === "number" && (
                            <div className="property-card-score-breakdown-row">
                                <span className="xs-label colour-primary">Base score</span>
                                <span className="xs-label-bold colour-primary">{Math.round(scoreBreakdown.baseScore)}</span>
                            </div>
                        )}
                        {(scoreBreakdown.bonusesApplied || []).map((b, i) => (
                            <div key={`b-${i}`} className="property-card-score-breakdown-row">
                                <span className="xs-label colour-primary">{labelWithNickname(b.key)}</span>
                                <span className="xs-label-bold property-card-score-breakdown-bonus">+{typeof b.value === "number" ? Math.round(b.value) : b.value}</span>
                            </div>
                        ))}
                        {(scoreBreakdown.penaltiesApplied || []).map((p, i) => (
                            <div key={`p-${i}`} className="property-card-score-breakdown-row">
                                <span className="xs-label colour-primary">{labelWithNickname(p.key)}</span>
                                <span className="xs-label-bold property-card-score-breakdown-penalty">{typeof p.value === "number" ? Math.round(p.value) : p.value}</span>
                            </div>
                        ))}
                        <div className="property-card-score-breakdown-row property-card-score-breakdown-total">
                            <span className="xs-label-bold colour-primary">Final score</span>
                            <span className="xs-label-bold colour-primary">{typeof scoreBreakdown.score === "number" ? Math.round(scoreBreakdown.score) : displayScore}</span>
                        </div>
                        {(() => {
                            const vs = curbAppealData?.vsSubjectBlock;
                            const impression = curbAppealData?.blockImpression;
                            const analysis = curbAppealData?.curbAppealAnalysis;
                            const PAIRWISE_LABELS = {
                                noticeably_better: "Comp block noticeably better than subject",
                                slightly_better: "Comp block slightly better than subject",
                                similar: "Comp block similar to subject",
                                slightly_worse: "Comp block slightly worse than subject",
                                noticeably_worse: "Comp block noticeably worse than subject"
                            };
                            const pairwiseLabel = typeof vs === "string" ? PAIRWISE_LABELS[vs] : null;
                            if (!pairwiseLabel && !impression && !analysis) return null;
                            return (
                                <div className="property-card-score-breakdown-row property-card-score-breakdown-block-context">
                                    <span className="xs-label-bold colour-primary">Block context</span>
                                    {pairwiseLabel && (
                                        <span className="xs-label colour-primary">{pairwiseLabel}</span>
                                    )}
                                    {impression && (
                                        <span className="xs-label colour-secondary">{impression}</span>
                                    )}
                                    {analysis && analysis !== impression && (
                                        <span className="xs-label colour-secondary">{analysis}</span>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                }
                {
                    testView === true &&
                    <div className="property-card-test-view-container">
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Overall = {percentage.format(property.compDetails?.overallScore ?? 0)}
                            </span>
                        }
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Location = {percentage.format(property.compDetails?.locationScore ?? 0)}
                            </span>
                        }
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Recency = {percentage.format(property.compDetails?.recencyScore ?? 0)}
                            </span>
                        }
                        {
                            subjectProperty === false &&
                            <span className="body-regular colour-primary">
                                Similarity = {percentage.format(property.compDetails?.similarityScore ?? 0)}
                            </span>
                        }
                        <span className="body-regular colour-primary">
                            Subdivision = {property.propertyAttributes.subdivision}
                        </span>
                        <span className="body-regular colour-primary">
                            Census block = {property.addressDetails.censusBlock}
                        </span>
                        <span className="body-regular colour-primary">
                            Geo ID = {property.addressDetails.geoId}
                        </span>
                        <span className="body-regular colour-primary">
                            Median home = {formatterCompact.format(property.economicData.tract.median_home_value)}
                        </span>
                        <span className="body-regular colour-primary">
                            Median income = {formatterCompact.format(property.economicData.tract.median_household_income)}
                        </span>
                        <span className="body-regular colour-primary">
                            Construction = {property.propertyAttributes.constructionTypeBySource.reapi === null ? property.propertyAttributes.constructionTypeBySource.zillow : property.propertyAttributes.constructionTypeBySource.reapi}
                        </span>
                        <span className="body-regular colour-primary">
                            Waterfront = {JSON.stringify(property.propertyAttributes.waterFront)}
                        </span>
                    </div>
                }
                <div 
                    className="property-card-image-container relative-container"
                    onMouseLeave={() => turnOffMap()}
                >
                    {
                        subjectProperty === true &&
                        <Badge
                            bgColour={"bg-colour-black property-card-subject-property-badge"}
                            text={"Subject property"}
                            statusDot={false}
                            statusDotColour={null}
                            textClass="body-semibold colour-white"
                            icon={<House fill={colour.gray00} className="" />}
                            tooltip={false}
                            tooltipBody={null}
                            tooltipPosition={null}
                            iconLeft={true}
                        />
                    }
                    {
                        subjectProperty === false && property.compDetails?.distanceMiles !== null && property.compDetails?.distanceMiles !== undefined &&
                        <Distance
                            value={`${property.compDetails.distanceMiles.toFixed(2)}mi`}
                            distanceHover={turnOnMap}
                            showMap={showMap}
                            hoverTimeout={distanceHoverTimeoutRef.current}
                            setHoverTimeout={(timeout) => { distanceHoverTimeoutRef.current = timeout; }}
                            darkMode={darkMode}
                        />
                    }
                    {
                        showMap === true && subjectProperty === false ?
                        <CompCardMap
                            isMapLoaded={isLoaded}
                            comp={property}
                            subjectProperty={subjectPropertyObject}
                            isMapActive={true}
                            bestComp={bestComp}
                        />
                        :
                        displayablePhotoUrls.length === 0 ?
                        <div 
                            className="property-card-no-image-container"
                        >
                            <Image
                                className="property-card-no-image-element"
                                fill={colour.gray05}
                            />
                            <span className="label-regular colour-secondary block-text text-align-center">
                                No image
                            </span>
                        </div>
                        :
                        displayablePhotoUrls.length === 1 ?
                        <div 
                            className="property-card-single-image-container"
                            style={{ backgroundImage: `url(${displayablePhotoUrls[0]})` }}
                        >
                        </div>
                        :
                        <CustomSwiper
                            images={displayablePhotoUrls}
                            imageLimit={100}
                            title=""
                            arrowClicked={null}
                            imageClick={handleImageClick}
                            subjectProperty={subjectProperty}
                        />
                    }
                    {
                        showDriverBadges && (topDrivers.topPositive || topDrivers.topNegative) &&
                        <div className="property-card-driver-badges-container">
                            {topDrivers.topPositive && topDrivers.topPositiveLabel && (
                                <Badge
                                    bgColour="bg-colour-jade-03-dark"
                                    text={topDrivers.topPositiveLabel}
                                    statusDot={false}
                                    statusDotColour={null}
                                    textClass="body-semibold colour-white"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={false}
                                />
                            )}
                            {topDrivers.topNegative && topDrivers.topNegativeLabel && (
                                <Badge
                                    bgColour="bg-colour-red-03-dark"
                                    text={topDrivers.topNegativeLabel}
                                    statusDot={false}
                                    statusDotColour={null}
                                    textClass="body-semibold colour-white"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={false}
                                />
                            )}
                        </div>
                    }
                    {
                        subjectProperty === false && selected === false && readOnly === false && showKeepRemoveButton === true ?
                        <Button
                            buttonType="primary-button"
                            size="medium"
                            text={"Keep"}
                            cta={toggleSelectedComp}
                            disabled={false}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                        :
                        subjectProperty === false && selected === true && readOnly === false && showKeepRemoveButton === true ?
                        <Button
                            buttonType="light-button"
                            size="medium"
                            text={"Remove"}
                            cta={toggleSelectedComp}
                            disabled={false}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                        :
                        null
                    }
                </div>
                <div
                    className={"property-card-body-container " + (subjectProperty === true ? "" : "cursor-pointer")}
                    onClick={handleBodyClick}
                >
                    {
                        priceCeiling === true &&
                        <Ceiling />
                    }
                    {
                        priceFloor === true &&
                        <Floor />
                    }
                    {
                        priceStaleActive === true &&
                        <StaleActive />
                    }
                    <div className="property-card-details-container">
                        {
                            bestComp === true ?
                            <div className="run-comps-subject-property-title-container desktop-none">
                                <div className="relative-container">
                                    <MapBubble
                                        className=""
                                        fill={colour.jade01}
                                        stroke={colour.jade02}
                                        strokeWidth="0.5"
                                    />
                                    <House
                                        fill={colour.jade03}
                                        className="all-comps-map-house-icon"
                                    />
                                </div>
                                <span className="label-regular-caps colour-primary">
                                    Suggested comp
                                </span>
                            </div>
                            :
                            null
                        }
                        <div className="property-card-price-container">
                            <h3 className={"heading-small-bold " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                {
                                    subjectProperty === true && purchasePrice !== null ?
                                    formatterLong.format(purchasePrice).replace(".00", "")
                                    :
                                    subjectProperty === true ?
                                    "N/A"
                                    :
                                    getPriceField(property) === null ? 
                                    "Price unavailable"
                                    :
                                    formatterLong.format(getPriceField(property)).replace(".00", "")
                                }
                            </h3>
                            {
                                property.mlsVerified === true &&
                                 <Badge
                                    bgColour={"bg-colour-flip"}
                                    text={"MLS VERIFIED"}
                                    statusDot={true}
                                    statusDotColour={colour.jade02}
                                    textClass="body-semibold colour-primary"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={false}
                                />
                            }
                        </div>
                        <span className={"body-regular block-text white-space-no-wrap " + (darkMode === true ? "colour-tertiary" : "colour-secondary")}>
                            {property.addressDetails.streetAddress}
                        </span>
                        <PropertyLinks
                            property={property}
                            darkMode={darkMode}
                            propertyEstimates={subjectProperty === true ? propertyEstimates : null}
                            sourceURL={subjectProperty === true ? sourceURL : null}
                            source={subjectProperty === true ? source : null}
                            subjectProperty={subjectProperty}
                        />
                    </div>
                    <div className="property-card-condition-container">
                        {
                            subjectProperty === false ?
                            (
                                showConditionBadge ?
                                <Condition
                                    label={finalCondition}
                                    iconBG={propertyConditionBG(finalCondition, darkMode)}
                                    darkMode={darkMode}
                                />
                                :
                                null
                            )
                            :
                            null
                        }
                        {
                            subjectProperty === false && propertyConditionFinish !== null &&
                            <FinishChip
                                label={propertyConditionFinish === "middle" ? "Mid-tier" : (propertyConditionFinish === "luxury" || propertyConditionFinish === "high") ? "Luxury" : "Lipstick"}
                                level={propertyConditionFinish === "middle" ? 1 : (propertyConditionFinish === "luxury" || propertyConditionFinish === "high") ? 2 : 0}
                            />
                        }
                        {
                            subjectProperty === true && remodelLevel !== null && remodelLevel !== undefined ?
                            <FinishChip
                                label={remodelLevel === 0 ? "Lipstick" : remodelLevel === 1 ? "Mid-tier" : "Luxury"}
                                level={remodelLevel}
                            />
                            :
                            null
                        }
                    </div>
                    <div className="property-card-details-column">
                        {
                            details.map((item, index) =>
                                item.label !== null &&
                                <div 
                                    className={`property-card-detail-element ${item.showGradeBreakdown ? "property-card-detail-with-breakdown" : ""}`}
                                    key={index}
                                >
                                    {
                                        item.showGradeBreakdown ?
                                        <div className="property-card-detail-with-breakdown-wrapper">
                                            <div className="property-card-detail-top-row">
                                                {item.icon}
                                                <span className={"body-regular " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                                    {item.label}
                                                    {
                                                        item.bold !== null &&
                                                        <span className={"body-semibold " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                                            {" "}{item.bold}
                                                        </span>
                                                    }
                                                </span>
                                                {
                                                    item.differenceNumber === null || item.differenceNumber === 0 || item.difference === "0%" || item.difference === "-0%" ?
                                                    null
                                                    :
                                                    item.showGradeBreakdown === true ?
                                                    null
                                                    :
                                                    index === 3 ?
                                                    <span className={"body-semibold " + (Math.abs(item.differenceNumber) >= 20 ? (darkMode === true ? "colour-red-02" : "colour-error") : Math.abs(item.differenceNumber) >= 10 ? "colour-yellow" : (darkMode === true ? "colour-green-02" : "colour-green"))}>
                                                        {item.differenceNumber > 0 ? "+" : ""}{item.difference}
                                                    </span>
                                                    :
                                                    <span className={"body-semibold " + (item.differenceNumber > 0 ? (darkMode === true ? "colour-green-02" : "colour-green") : (darkMode === true ? "colour-red-02" : "colour-error"))}>
                                                        {item.differenceNumber > 0 ? "+" : ""}{item.difference}
                                                    </span>
                                                }
                                            </div>
                                            <div className="property-card-grade-breakdown-content">
                                                <div className="property-card-grade-breakdown-vertical-line" />
                                                <div className="property-card-grade-breakdown-sections">
                                                    <div className="property-card-grade-breakdown-section">
                                                        <div className="property-card-grade-breakdown-horizontal-line" />
                                                        <span className={"body-regular " + (darkMode === true ? "colour-tertiary" : "colour-secondary")}>
                                                            Above grade
                                                        </span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span className={"body-regular " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                                                {numberFormatter.format(aboveGradeFinishedArea)}
                                                            </span>
                                                            {
                                                                subjectProperty === false && aboveGradeFinishedArea !== null && subjectSquareFootageForComparison !== null && subjectSquareFootageForComparison > 0 &&
                                                                (() => {
                                                                    const aboveGradeDiff = ((aboveGradeFinishedArea - subjectSquareFootageForComparison) / subjectSquareFootageForComparison);
                                                                    const aboveGradeDiffNumber = aboveGradeFinishedArea - subjectSquareFootageForComparison;
                                                                    if (aboveGradeDiffNumber === 0 || aboveGradeDiff === 0) return null;
                                                                    return (
                                                                        <span className={"body-semibold " + (aboveGradeDiffNumber > 0 ? (darkMode === true ? "colour-green-02" : "colour-green") : (darkMode === true ? "colour-red-02" : "colour-error"))}>
                                                                            {aboveGradeDiffNumber > 0 ? "+" : ""}{percentageCompact.format(aboveGradeDiff)}
                                                                        </span>
                                                                    );
                                                                })()
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className="property-card-grade-breakdown-section">
                                                        <div className="property-card-grade-breakdown-horizontal-line" />
                                                        <span className={"body-regular " + (darkMode === true ? "colour-tertiary" : "colour-secondary")}>
                                                            Below grade
                                                        </span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            {subjectProperty === true && (belowGradeFinishedArea === null || belowGradeFinishedArea === undefined || belowGradeFinishedArea === 0) && readOnly === false && onOpenBasementModal !== null ? (
                                                                <span 
                                                                    className={"body-regular text-link cursor-pointer underline " + (darkMode === true ? "colour-white" : "colour-primary")}
                                                                    onClick={onOpenBasementModal}
                                                                >
                                                                    Add
                                                                </span>
                                                            ) : (
                                                                <span className={"body-regular " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                                                    {belowGradeFinishedArea !== null ? numberFormatter.format(belowGradeFinishedArea) : "N/A"}
                                                                </span>
                                                            )}
                                                            {
                                                                subjectProperty === false && belowGradeFinishedArea !== null && belowGradeFinishedArea > 0 && subjectBelowGradeForComparison !== null && subjectBelowGradeForComparison > 0 &&
                                                                (() => {
                                                                    const belowGradeDiff = ((belowGradeFinishedArea - subjectBelowGradeForComparison) / subjectBelowGradeForComparison);
                                                                    const belowGradeDiffNumber = belowGradeFinishedArea - subjectBelowGradeForComparison;
                                                                    if (belowGradeDiffNumber === 0 || belowGradeDiff === 0) return null;
                                                                    return (
                                                                        <span className={"body-semibold " + (belowGradeDiffNumber > 0 ? (darkMode === true ? "colour-green-02" : "colour-green") : (darkMode === true ? "colour-red-02" : "colour-error"))}>
                                                                            {belowGradeDiffNumber > 0 ? "+" : ""}{percentageCompact.format(belowGradeDiff)}
                                                                        </span>
                                                                    );
                                                                })()
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        :
                                        <>
                                            {item.icon}
                                            <div className="property-card-detail-content">
                                                <div className="property-card-detail-label-row">
                                                    <span className={"body-regular " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                                        {item.label}
                                                        {
                                                            item.bold !== null &&
                                                            <span className={"body-semibold " + (darkMode === true ? "colour-white" : "colour-primary")}>
                                                                {" "}{item.bold}
                                                            </span>
                                                        }
                                                    </span>
                                                    {
                                                        item.differenceNumber === null || item.differenceNumber === 0 || item.difference === "0%" || item.difference === "-0%" ?
                                                        null
                                                        :
                                                        index === 3 ?
                                                        <span className={"body-semibold " + (Math.abs(item.differenceNumber) >= 20 ? (darkMode === true ? "colour-red-02" : "colour-error") : Math.abs(item.differenceNumber) >= 10 ? "colour-yellow" : (darkMode === true ? "colour-green-02" : "colour-green"))}>
                                                            {item.differenceNumber > 0 ? "+" : ""}{item.difference}
                                                        </span>
                                                        :
                                                        <span className={"body-semibold " + (item.differenceNumber > 0 ? (darkMode === true ? "colour-green-02" : "colour-green") : (darkMode === true ? "colour-red-02" : "colour-error"))}>
                                                            {item.differenceNumber > 0 ? "+" : ""}{item.difference}
                                                        </span>
                                                    }
                                                </div>
                                            </div>
                                        </>
                                    }
                                </div>
                            )
                        }
                    </div>
                    {
                        listingDescription !== null &&
                        <div
                            className="property-card-listing-description"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                className="property-card-listing-description-toggle cursor-pointer"
                                onClick={handleDescriptionToggle}
                                aria-expanded={descriptionOpen}
                            >
                                <HugeiconsIcon icon={Note01Icon} size={16} color={darkMode === true ? colour.gray04 : colour.gray05} />
                                <span className={"body-regular " + (darkMode === true ? "colour-tertiary" : "colour-secondary")}>
                                    Listing description
                                </span>
                                <Chevron
                                    fill={darkMode === true ? colour.gray04 : colour.gray05}
                                    className={"property-card-listing-description-chevron " + (descriptionOpen === true ? "rotate-90" : "")}
                                />
                            </button>
                            {
                                descriptionOpen === true &&
                                <span className={"body-regular block-text property-card-listing-description-body " + (darkMode === true ? "colour-tertiary" : "colour-secondary")}>
                                    {listingDescription}
                                </span>
                            }
                        </div>
                    }
                    {
                        subjectProperty === false &&
                        <div className="property-card-badges-row">
                            <Badge
                                bgColour={propertyStatusBG(property, darkMode)}
                                text={propertyStatusText(property)}
                                statusDot={true}
                                statusDotColour={getStatusDotColour(property)}
                                textClass={"body-semibold " + (darkMode === true ? "colour-white" : "colour-primary")}
                                icon={null}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition={null}
                                iconLeft={false}
                            />
                            {
                                (flipStatus === "Flip" || flipStatus === "Flop") &&
                                <div className="relative-container">
                                    <Badge
                                        bgColour={flipBGColour}
                                        text={flipStatus}
                                        statusDot={true}
                                        statusDotColour={flipDotcolour}
                                        textClass={"body-semibold " + (darkMode === true ? "colour-white" : "colour-primary")}
                                        icon={null}
                                        tooltip={true}
                                        tooltipBody={flipDescription}
                                        tooltipPosition={"top"}
                                        iconLeft={false}
                                    />
                                </div>
                            }
                        </div>
                    }
                    {
                        subjectProperty === false && property.novationTimeline !== undefined && property.novationTimeline !== null && property.novationTimeline.length > 0 &&
                        <NovationTimeline
                            property={property}
                            innerWidth={innerWidth}
                            darkMode={darkMode}
                        />
                    }
                </div>
                {
                    priceCeiling === true && priceCeilingText !== null ?
                    <div 
                        className="property-card-disqualifier-container cursor-pointer"
                        onClick={handleDisqualifierClick}
                    >
                        <Disqualifier
                            text={`**Price ceiling** - ${priceCeilingText}`}
                            loading={false}
                            key={priceCeilingText}
                            allComps={allComps}
                        />
                    </div>
                    :
                    subjectProperty === false && (clickedCompDescription !== null || gptLoading === true) ?
                    <div 
                        className="property-card-disqualifier-container cursor-pointer"
                        onClick={handleDisqualifierClick}
                    >
                        <Disqualifier
                            text={clickedCompDescription}
                            loading={gptLoading}
                            key={clickedCompDescription}
                            allComps={allComps}
                        />
                    </div>
                    :
                    null
                }
                {/* {
                    subdivisionObject &&
                    <SubdivisionWarning
                        subdivisionObject={subdivisionObject}
                    />
                } */}
            </div>
        </div>
    )
};

export default React.memo(PropertyCard);